import React, { createContext, useState, useCallback, useContext, useEffect, useMemo } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { IClusterHMIProps } from "../../components/ClusterHMICard/ClusterHMICardInterfaces";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { getClusterIdHMI } from "utilities/storageUtilities";

interface IClusterDragDropContext {
    isDragModeActive: boolean;
    toggleDragMode: () => void | Promise<void>;
    clusterHMIs: IClusterHMIProps["machineCenter"][];
    onDragEnd: (event: DragEndEvent) => void;
    isSaving: boolean;
    cancelDragMode: () => void;
}

interface IClusterDragDropContextProps {
    children: React.ReactNode;
}

const initialState: IClusterDragDropContext = {
    isDragModeActive: false,
    toggleDragMode: () => {},
    clusterHMIs: [],
    onDragEnd: () => {},
    isSaving: false,
    cancelDragMode: () => {},
};

export const ClusterDragDropContext = createContext<IClusterDragDropContext>(initialState);

export const ClusterDragDropContextProvider: React.FC<IClusterDragDropContextProps> = ({ children }) => {
    const { store: clusterStore, dispatch } = useContext(ClusterCachedDataContext);
    const { publishClusterReordering } = useContext(MQTTBrokerContext);
    const [isDragModeActive, setIsDragModeActive] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const clusterHMIsInitial: IClusterHMIProps["machineCenter"][] = useMemo(
        () =>
            clusterStore.machineCentersCluster.map((machineCenter) => {
                return {
                    id: machineCenter.machine_center_id,
                    name: machineCenter.name,
                    hmiCode: machineCenter.hmi_code,
                    plcAddress: machineCenter.plc_address,
                    downtimeMethodCapture: machineCenter.downtime_method_capture_id,
                    downtimeTriggerThreshold: machineCenter.downtime_trigger_threshold,
                    percentages: machineCenter.last_two_shifts_event_reports
                        ? {
                              uptime: machineCenter.last_two_shifts_event_reports?.currentShiftTimes.uptime,
                              downtime: machineCenter.last_two_shifts_event_reports?.currentShiftTimes.downtime,
                              shift: machineCenter.last_two_shifts_event_reports?.currentShiftTimes.shift,
                          }
                        : undefined,
                    isBottleneck: machineCenter.is_bottleneck,
                } as IClusterHMIProps["machineCenter"];
            }),
        [clusterStore.machineCentersCluster],
    );

    const [clusterHMIs, setClusterHMIs] = useState<IClusterHMIProps["machineCenter"][]>(clusterHMIsInitial);
    const [originalOrder, setOriginalOrder] = useState<IClusterHMIProps["machineCenter"][]>(clusterHMIsInitial);

    useEffect(() => {
        if (clusterHMIsInitial.length === 0) {
            return;
        }

        const currentIds = clusterHMIs.map((hmi) => hmi.id).sort();
        const initialIds = clusterHMIsInitial.map((hmi) => hmi.id).sort();
        const idsMatch = JSON.stringify(currentIds) === JSON.stringify(initialIds);

        if (!idsMatch || clusterHMIs.length === 0) {
            setClusterHMIs(clusterHMIsInitial);
            setOriginalOrder(clusterHMIsInitial);
            return;
        }

        if (isDragModeActive) {
            return;
        }

        const freshById = new Map(clusterHMIsInitial.map((hmi) => [hmi.id, hmi]));
        const mergedClusterHMIs = clusterHMIs.map((hmi) => freshById.get(hmi.id) ?? hmi);
        if (JSON.stringify(mergedClusterHMIs) !== JSON.stringify(clusterHMIs)) {
            setClusterHMIs(mergedClusterHMIs);
            setOriginalOrder((previousOrder) => previousOrder.map((hmi) => freshById.get(hmi.id) ?? hmi));
        }
    }, [clusterHMIsInitial, clusterHMIs, isDragModeActive]);

    const hasUnsavedChanges = useMemo(() => {
        if (clusterHMIs.length === 0 || originalOrder.length === 0) return false;
        return JSON.stringify(clusterHMIs.map((hmi) => hmi.id)) !== JSON.stringify(originalOrder.map((hmi) => hmi.id));
    }, [clusterHMIs, originalOrder]);

    const onDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (active.id !== over?.id && clusterHMIs) {
                const oldIndex = clusterHMIs.findIndex((hmi) => hmi.id.toString() === active.id);
                const newIndex = clusterHMIs.findIndex((hmi) => hmi.id.toString() === over?.id);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newClusterHMIs = arrayMove(clusterHMIs, oldIndex, newIndex);
                    setClusterHMIs(newClusterHMIs);
                }
            }
        },
        [clusterHMIs],
    );

    const cancelDragMode = useCallback(() => {
        setClusterHMIs(originalOrder);
        setIsDragModeActive(false);
    }, [originalOrder]);

    const toggleDragMode = useCallback(async () => {
        if (isDragModeActive) {
            // Turning drag mode OFF
            if (hasUnsavedChanges) {
                const clusterId = getClusterIdHMI();
                if (clusterId) {
                    const orderedMachineCenterIds = clusterHMIs.map((hmi) => hmi.id);
                    setIsSaving(true);
                    try {
                        await publishClusterReordering({
                            clusterId,
                            orderedMachineCenterIds,
                            timestamp: Date.now(),
                        });
                        dispatch.reorderMachineCentersCluster(orderedMachineCenterIds);
                        setOriginalOrder(clusterHMIs);
                    } catch (error) {
                        console.error("Failed to send cluster reordering:", error);
                        setClusterHMIs(originalOrder);
                    } finally {
                        setIsSaving(false);
                    }
                }
            }
            setIsDragModeActive(false);
        } else {
            // Turning drag mode ON
            setOriginalOrder(clusterHMIs);
            setIsDragModeActive(true);
        }
    }, [isDragModeActive, clusterHMIs, hasUnsavedChanges, publishClusterReordering, dispatch, originalOrder]);

    const providerValue: IClusterDragDropContext = {
        isDragModeActive,
        toggleDragMode,
        clusterHMIs,
        onDragEnd,
        cancelDragMode,
        isSaving,
    };

    return <ClusterDragDropContext.Provider value={providerValue}>{children}</ClusterDragDropContext.Provider>;
};
